import { NBSP, ONE_SIZE_KEYS, SIZE_DELIMITER, SIZE_DELIMITER_FALLBACK, WHITESPACE } from './b2b.constants'

import type { AnyLineItem } from './b2b.types'

const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'] as const
// Fast lookup for sorting known sizes.
// Example: SIZE_ORDER_INDEX["XS"] -> 0, SIZE_ORDER_INDEX["3XL"] -> 6.
const SIZE_ORDER_INDEX = SIZE_ORDER.reduce<Record<string, number>>((acc, size, index) => {
  acc[size] = index
  return acc
}, {})
const KNOWN_SIZE_LOOKUP = new Map<string, string>(SIZE_ORDER.map(size => [size, size]))

const NUMERIC_SIZE_REGEX = /^\d+$/

/** Returns true when a size option value represents a one-size product (e.g. "One Size", "OS", "default"). */
export const isOneSizeValue = (size: string): boolean => ONE_SIZE_KEYS.has(size.replace(NBSP, ' ').trim().toUpperCase())

/**
 * Multi-level size comparator.
 * Order: One Size → standard sizes (XS–5XL) → numeric (ascending) → alphabetical.
 */
export const compareSizeKeys = (a: string, b: string): number => {
  const aUpper = a.toUpperCase()
  const bUpper = b.toUpperCase()

  const aIsOneSize = ONE_SIZE_KEYS.has(aUpper)
  const bIsOneSize = ONE_SIZE_KEYS.has(bUpper)
  if (aIsOneSize && !bIsOneSize) return -1
  if (!aIsOneSize && bIsOneSize) return 1

  // Handle composite sizes like "M/L", "XS/S" by comparing the first part
  const aKey = aUpper.includes('/') ? aUpper.split('/')[0].trim() : aUpper
  const bKey = bUpper.includes('/') ? bUpper.split('/')[0].trim() : bUpper

  const aIndex = SIZE_ORDER_INDEX[aKey] ?? -1
  const bIndex = SIZE_ORDER_INDEX[bKey] ?? -1
  if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex
  if (aIndex !== -1) return -1
  if (bIndex !== -1) return 1

  const aIsNumeric = NUMERIC_SIZE_REGEX.test(a)
  const bIsNumeric = NUMERIC_SIZE_REGEX.test(b)
  if (aIsNumeric && bIsNumeric) return parseInt(a, 10) - parseInt(b, 10)
  if (aIsNumeric && !bIsNumeric) return -1
  if (!aIsNumeric && bIsNumeric) return 1

  return a.localeCompare(b)
}

/** Returns true when every size option on the product is a one-size label — i.e. a one-size category. */
export const isOneSizeProduct = (optionValues: readonly { value: string }[]): boolean => {
  if (optionValues.length === 0) return false
  return optionValues.every(option => isOneSizeValue(option.value))
}

/**
 * True when the B2B self-serve size section should use the shorter "Enter Quantity" label
 * (one-size category or only a single size option such as "S").
 */
export const shouldUseEnterQuantityLabel = (optionValues: readonly { value: string }[]): boolean => {
  if (optionValues.length === 0) return false
  return optionValues.length === 1 || isOneSizeProduct(optionValues)
}

/**
 * Extracts the size token from variant title.
 * When knownColorValue is provided, filters it out so the remaining part is the size.
 * This handles both "Color / Size" and "Size / Color" variant title formats.
 *
 * @example
 * extractSizeFromVariantTitle("Navy / XS") // "XS"
 * extractSizeFromVariantTitle("S/M / Navy", "Navy") // "S/M"
 * extractSizeFromVariantTitle("Navy / XS / Regular") // "Regular"
 * extractSizeFromVariantTitle("Single") // null
 */
export const extractSizeFromVariantTitle = (variantTitle?: string | null, knownColorValue?: string): string | null => {
  if (typeof variantTitle !== 'string') return null

  const normalizedVariantTitle = variantTitle.replace(NBSP, ' ').trim()
  if (!normalizedVariantTitle) return null

  // Prefer space-delimited split to preserve compound sizes like "S/M"
  let parts = normalizedVariantTitle.split(SIZE_DELIMITER)
  if (parts.length < 2) {
    parts = normalizedVariantTitle.split(SIZE_DELIMITER_FALLBACK)
    if (parts.length < 2) return null
  }

  // If we know the color, exclude it so the remaining part is the size
  if (knownColorValue) {
    const colorLower = knownColorValue.toLowerCase().trim()
    const sizeParts = parts.filter(p => p.trim().toLowerCase() !== colorLower)
    if (sizeParts.length > 0) return sizeParts[0]?.trim() ?? null
  }

  const size = parts[parts.length - 1]?.trim()
  return size ? size : null
}

/**
 * Normalizes an extracted size token.
 * - Canonicalizes known sizes to uppercase (e.g. " xs " -> "XS")
 * - Keeps unknown but non-empty sizes as-is after trimming
 *
 * @example
 * normalizeSize(" xS ") // "XS"
 * normalizeSize(" one size ") // "one size"
 */
const normalizeSize = (size: string): string | null => {
  const normalizedSize = size.replace(NBSP, ' ').trim().replace(WHITESPACE, ' ')
  if (!normalizedSize) return null

  const uppercaseSize = normalizedSize.toUpperCase()
  return KNOWN_SIZE_LOOKUP.get(uppercaseSize) ?? normalizedSize
}

/**
 * Converts incoming quantity into a positive integer.
 * Returns null for invalid values (<=0, non-integer, NaN).
 *
 * @example
 * toValidQuantity(3) // 3
 * toValidQuantity("4") // 4
 * toValidQuantity("2.5") // null
 */
const toValidQuantity = (quantity: unknown): number | null => {
  const parsedQuantity = typeof quantity === 'string' ? Number(quantity.trim()) : quantity
  if (typeof parsedQuantity !== 'number' || !Number.isInteger(parsedQuantity) || parsedQuantity <= 0) return null
  return parsedQuantity
}

/**
 * Builds a size->quantity map from cart line items.
 * - Skips items with invalid/missing size
 * - Skips invalid quantities
 * - Aggregates duplicate sizes
 * - Sorts known sizes by predefined order; unknown sizes come after known sizes
 *
 * @example
 * buildSizeBreakdown([
 *   { variantMetaData: { variantTitle: "Navy / S" }, quantity: 2 } as AnyLineItem,
 *   { variantMetaData: { variantTitle: "Navy / xs" }, quantity: 1 } as AnyLineItem,
 * ])
 * // { XS: 1, S: 2 }
 *
 * // With knownColorValue, handles "Size / Color" format:
 * buildSizeBreakdown([
 *   { variantMetaData: { variantTitle: "S/M / Navy" }, quantity: 4 } as AnyLineItem,
 * ], "Navy")
 * // { "S/M": 4 }
 */
export const buildSizeBreakdown = (lineItems: AnyLineItem[], knownColorValue?: string): Record<string, number> => {
  const breakdown = lineItems.reduce<Record<string, number>>((acc, lineItem) => {
    const size = extractSizeFromVariantTitle(lineItem.variantMetaData?.variantTitle, knownColorValue)
    const normalizedSize = size ? normalizeSize(size) : null
    const quantity = toValidQuantity((lineItem as { quantity?: unknown }).quantity)
    if (!normalizedSize || quantity === null) return acc

    acc[normalizedSize] = (acc[normalizedSize] ?? 0) + quantity
    return acc
  }, {})

  return Object.fromEntries(Object.entries(breakdown).sort(([a], [b]) => compareSizeKeys(a, b)))
}

/**
 * Normalizes formatted quantity values for compact size display.
 * - Invalid numbers and negatives are clamped to 0
 * - Valid finite numbers are returned as-is
 *
 * @example
 * sanitizeBreakdownQuantity(5) // 5
 * sanitizeBreakdownQuantity(-2) // 0
 * sanitizeBreakdownQuantity(Number.NaN) // 0
 */
const sanitizeBreakdownQuantity = (quantity: number): number =>
  Number.isFinite(quantity) && quantity >= 0 ? quantity : 0

/**
 * Normalizes size labels for compact display.
 * - Trims whitespace and NBSP from labels
 * - Drops empty labels
 *
 * @example
 * normalizeBreakdownLabel(" XS ") // "XS"
 * normalizeBreakdownLabel("   ") // null
 */
const normalizeBreakdownLabel = (size: string): string | null => {
  const normalizedSize = size.replace(NBSP, ' ').trim()
  return normalizedSize ? normalizedSize : null
}

/**
 * Formats size breakdown object into compact "Size: Qty" text.
 * Output order follows the incoming object key iteration order.
 *
 * @example
 * formatSizeBreakdown({ XS: 10, S: 15, M: 5 }) // "XS: 10 | S: 15 | M: 5"
 * formatSizeBreakdown({ XS: -1, M: Number.NaN }) // "XS: 0 | M: 0"
 */
export const formatSizeBreakdown = (breakdown: Record<string, number>): string =>
  Object.entries(breakdown)
    .sort(([a], [b]) => compareSizeKeys(a, b))
    .map(([size, quantity]) => {
      const normalizedSize = normalizeBreakdownLabel(size)
      if (!normalizedSize) return null

      return `${normalizedSize}: ${sanitizeBreakdownQuantity(quantity)}`
    })
    .filter((entry): entry is string => Boolean(entry))
    .join(' | ')
